import { useMemo, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { AuthStatus } from '../components/AuthStatus';
import { Logo } from '../components/Logo';
import { IS_GOOGLE_AUTH_ENABLED } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  baseOpacity: number;
  minOpacity: number;
  twinkle: number;
}

function Stars(): JSX.Element {
  const stars = useMemo<Star[]>(
    () => Array.from({ length: 100 }, (_, id) => {
      const baseOpacity = Math.min(Math.random() * 0.8 + 0.4, 1);
      const y = Math.random() < 0.7 ? Math.random() * 60 : 60 + Math.random() * 40;

      return {
        id,
        x: Math.random() * 100,
        y,
        size: Math.random() * 2.5 + 0.5,
        baseOpacity,
        minOpacity: Math.min(baseOpacity * 0.4, 0.8),
        twinkle: Math.random() * 2 + 1,
      };
    }),
    [],
  );

  return (
    <>
      <style>
        {`
          @keyframes starTwinkle {
            0%, 100% { opacity: var(--star-max-opacity); }
            50% { opacity: var(--star-min-opacity); }
          }
        `}
      </style>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              '--star-max-opacity': star.baseOpacity.toString(),
              '--star-min-opacity': star.minOpacity.toString(),
              opacity: star.baseOpacity,
              animation: `starTwinkle ${star.twinkle}s ease-in-out infinite`,
              boxShadow: `0 0 ${star.size * 2}px rgba(255, 255, 255, ${star.baseOpacity})`,
            } as CSSProperties & { '--star-max-opacity': string; '--star-min-opacity': string }}
          />
        ))}
      </div>
    </>
  );
}

export function HomePage(): JSX.Element {
  const { user } = useAuth();
  const shouldShowAuthStatus = IS_GOOGLE_AUTH_ENABLED || !!user;

  return (
    <div className="min-h-[calc(100vh-4.5rem)] flex items-center justify-center relative dark:from-gray-800 dark:to-gray-900 px-4 py-12">
      <div
        className="absolute inset-0 dark:hidden"
        style={{
          background: 'linear-gradient(to bottom, #000000 0%, #0a0a1a 15%, #312e81 30%, #6366f1 50%, #c084fc 70%, #fbcfe8 85%, #fed7aa 100%)',
        }}
      />

      <div className="dark:hidden">
        <Stars />
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 hidden dark:block" />

      <div className="max-w-md w-full relative z-10">
        <div
          className="rounded-2xl p-8 backdrop-blur-md border border-white/20 shadow-2xl"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <div className="text-center">
            <Logo size="lg" className="mb-2" logoWidth="w-7" />
            <p className="text-lg text-white dark:text-gray-300 mb-8">
              Conduct team retrospectives with real-time collaboration
            </p>
          </div>

          <div className="space-y-4">
            <Link to="/create" className="w-full btn-primary block text-center">
              Create New Board
            </Link>

            <div className="text-center">
              <p className="text-sm text-white/90 dark:text-gray-400">
                Or join an existing board with a link
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <h2 className="text-xl font-semibold text-white dark:text-white mb-4">
              Features
            </h2>
            <div className="space-y-2 text-sm text-white/90 dark:text-gray-300">
              <p><span aria-hidden="true">&#10003;</span> Real-time collaboration</p>
              <p><span aria-hidden="true">&#10003;</span> Customizable board templates</p>
              <p><span aria-hidden="true">&#10003;</span> Anonymous participation</p>
              <p><span aria-hidden="true">&#10003;</span> Mobile-friendly interface</p>
            </div>
          </div>

          {shouldShowAuthStatus && (
            <div className="mt-8 flex justify-center border-t border-white/15 pt-5">
              <AuthStatus compact signInAlign="center" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
